import classNames from "classnames";
import React, { useCallback, useLayoutEffect, useState } from "react";
import closeIcon from "../assets/images/close.svg";
import useTranslations from "../hooks/useTranslations";
import useUrl from "../hooks/useUrl";
import { LinkInfo } from "../models/shared/LinkInfo";
import { isNullOrWhiteSpace } from "../shared/string";
import { isValidLink } from "../utilities/links";
import Button, { ButtonSize } from "./Buttons/Button";
import { renderLinkTitle } from "./SanityLink";

export type SiteWideBannerColor = "primary" | "primary-light" | "secondary" | "tertiary";

export interface SiteWideBannerProps {
  buttonLink?: LinkInfo;
  color?: SiteWideBannerColor;
  currentPagePath?: string;
  enabled?: boolean;
  isSiteWide?: boolean;
  isStartPage?: boolean;
  isPermanent?: boolean;
  text?: string;
}

export const SiteWideBanner = ({
  buttonLink,
  color = "primary",
  currentPagePath,
  enabled,
  isSiteWide,
  isStartPage,
  isPermanent,
  text
}: SiteWideBannerProps) => {
  const translations = useTranslations();

  const buttonUrl = useUrl(buttonLink);

  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    sessionStorage.setItem("banner-dismissed", "true");
  }, []);

  useLayoutEffect(() => {
    if (sessionStorage.getItem("banner-dismissed") !== "true" || isPermanent) {
      if (enabled && (isSiteWide || isStartPage) && buttonUrl !== currentPagePath) {
        setVisible(true);
      }
    }
    // We only want this effect to run on mount - basically to only show the banner after hydration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let buttonColor: string;

  switch (color) {
    case "primary":
    case "tertiary":
      buttonColor = "primary";
      break;
    case "primary-light":
      buttonColor = "primary-light";
      break;
    case "secondary":
      buttonColor = "secondary";
      break;
  }

  const buttonVisible = isValidLink(buttonLink ?? undefined);

  return visible && (buttonVisible || !isNullOrWhiteSpace(text)) ? (
    <div
      className={classNames(
        "sticky top-12 w-full z-30 flex p-4 gap-4",
        "sm:justify-center sm:items-center",
        "xl:top-20",
        {
          "bg-brand-primary-95": color === "primary" || color === "primary-light",
          "bg-brand-secondary-90": color === "secondary",
          "bg-brand-tertiary-70": color === "tertiary"
        }
      )}
    >
      {/* Flex counterweight for the close button */}
      {!isPermanent && <div className="sm:flex-1"></div>}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6 lg:gap-8">
        {!isNullOrWhiteSpace(text) && (
          <p className="mb-0 text-brand-primary-20 font-bold">{text}</p>
        )}

        {buttonVisible && buttonLink && (
          <Button {...buttonLink} color={buttonColor} size={ButtonSize.Small} type="link">
            {renderLinkTitle(buttonLink)}
          </Button>
        )}
      </div>
      {!isPermanent && (
        <div className="flex-1 flex justify-end items-start">
          <button
            aria-label={translations.close ?? undefined}
            className="-mt-2 -mr-2 p-4 sm:m-0"
            onClick={dismiss}
          >
            <svg width={12} height={12} className="fill-current">
              <use xlinkHref={closeIcon.url} />
            </svg>
          </button>
        </div>
      )}
    </div>
  ) : null;
};

